import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePageState } from "@/hooks/usePageState";
import {
  pageStateQueryOptions,
  PageAccessError,
} from "@/lib/queries";
import { promptsListQueryOptions } from "@/lib/queries";
import { PromptsPage } from "@/features/prompts/components/PromptsPage";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";

/** `usedIn` pre-filters the list to one call site, e.g. from Token Usage → Switch model. */
const promptsSearchSchema = z.object({
  usedIn: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/prompts")({
  validateSearch: (search: Record<string, unknown>) => promptsSearchSchema.parse(search),
  beforeLoad: () => assertPagePermission("prompts"),
  loader: async ({ context }) => {
    const pageState = await context.queryClient
      .ensureQueryData(pageStateQueryOptions("prompts"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });

    // Warm the cache
    void context.queryClient.prefetchQuery(promptsListQueryOptions());

    return pageState;
  },
  component: PromptsRoute,
  errorComponent: PageAccessErrorComponent,
});

function PromptsRoute() {
  useDocumentTitle("Prompts");
  const { isLoading, error } = usePageState("prompts");
  const { usedIn } = Route.useSearch();

  if (isLoading) {
    return <div className="flex h-full items-center justify-center">Loading…</div>;
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <PromptsPage initialUsedIn={usedIn} />;
}
